import { Mail, Phone, MapPin, Linkedin, ArrowRight } from "lucide-react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import { SITE } from "@/lib/siteConfig";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-[#F4F7FC] py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-5xl mx-auto text-center">
          <SectionEyebrow className="mb-6">Contact</SectionEyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1E2761] tracking-tight leading-tight">
            Let's Start Your<br />
            <span className="italic">Next Chapter.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-[#334155]/85 leading-relaxed max-w-2xl mx-auto">
            Reach out directly. Whether you are a senior leader considering
            coaching, or a professional in transition, we usually reply within
            one business day.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="bg-white py-20 md:py-28 px-6 md:px-10" data-testid="contact-grid">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {/* Email */}
          <a
            href={`mailto:${SITE.email}`}
            data-testid="contact-email-card"
            className="bg-white p-10 md:p-12 hover:bg-[#F4F7FC] transition-colors group flex flex-col"
          >
            <div className="w-12 h-12 border border-[#C9A84C] flex items-center justify-center mb-6 text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-white transition-colors">
              <Mail size={20} strokeWidth={1.5} />
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#334155]/60 mb-2">
              Email Us
            </div>
            <div className="font-display text-xl md:text-2xl text-[#1E2761] tracking-tight break-words">
              {SITE.email}
            </div>
            <div className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1E2761] group-hover:text-[#C9A84C] transition-colors">
              Write to us <ArrowRight size={14} />
            </div>
          </a>

          {/* Phone */}
          <a
            href={`tel:${SITE.phoneRaw}`}
            data-testid="contact-phone-card"
            className="bg-white p-10 md:p-12 hover:bg-[#F4F7FC] transition-colors group flex flex-col"
          >
            <div className="w-12 h-12 border border-[#C9A84C] flex items-center justify-center mb-6 text-[#C9A84C] group-hover:bg-[#C9A84C] group-hover:text-white transition-colors">
              <Phone size={20} strokeWidth={1.5} />
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#334155]/60 mb-2">
              Call Us
            </div>
            <div className="font-display text-xl md:text-2xl text-[#1E2761] tracking-tight">
              {SITE.phone}
            </div>
            <div className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1E2761] group-hover:text-[#C9A84C] transition-colors">
              Mon to Fri, 09:00 to 18:00 CET <ArrowRight size={14} />
            </div>
          </a>

          {/* Location */}
          <div className="bg-white p-10 md:p-12 flex flex-col" data-testid="contact-location-card">
            <div className="w-12 h-12 border border-[#C9A84C] flex items-center justify-center mb-6 text-[#C9A84C]">
              <MapPin size={20} strokeWidth={1.5} />
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#334155]/60 mb-2">
              Based In
            </div>
            <div className="font-display text-xl md:text-2xl text-[#1E2761] tracking-tight">
              Eindhoven,<br />The Netherlands
            </div>
            <div className="mt-auto pt-6 text-sm text-[#334155]/80 leading-relaxed">
              Serving expat outplacement clients across the Netherlands.
              Performance coaching offered globally.
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#F4F7FC] py-20 md:py-28 px-6 md:px-10" data-testid="map-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <SectionEyebrow className="mb-4">Where to Find Us</SectionEyebrow>
              <h2 className="font-display text-3xl md:text-4xl text-[#1E2761] tracking-tight">
                Eindhoven, Netherlands
              </h2>
            </div>
            <a
              href={SITE.linkedinBrand}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="inline-flex items-center gap-3 border border-[#1E2761] text-[#1E2761] px-6 py-3 font-medium tracking-wide hover:bg-white transition-colors self-start"
            >
              <Linkedin size={16} />
              Connect on LinkedIn
            </a>
          </div>
          <div className="aspect-[16/8] border border-[#E2E8F0] overflow-hidden">
            <iframe
              title="Eindhoven location map"
              src="https://www.google.com/maps?q=Eindhoven,Netherlands&hl=en&z=12&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* CLOSING NOTE */}
      <section className="bg-[#1E2761] text-white py-20 md:py-28 px-6 md:px-10" data-testid="contact-closing">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-display text-2xl md:text-4xl leading-snug tracking-tight italic text-white">
            "Every great next chapter starts with a single, honest conversation."
          </p>
          <div className="mt-6 text-sm text-[#C9A84C]">
            {SITE.founder}, {SITE.founderTitle}
          </div>
        </div>
      </section>
    </div>
  );
}
